import { DeleteIcon, FormEditIcon, ResetIcon } from 'assets/icons';
import React, { useState } from 'react';
import EditReferralExpenseModal from './EditReferralExpenseModal';
import { JobExternalCompany } from 'containers/jobs/jobs.model';
import { GenericDeleteModal } from 'components';
import { CommissionBaseTypeLabels, CommissionType } from 'containers/settings/commission-settings/constants';
import { selectCurrentTeamAccountingPermissions, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { JobStatus } from 'containers/jobs/constant';
import { getAmountWithCurrency } from 'utils/amountUtils';

interface ExternalCompanyReferralTableProps {
  referralData?: JobExternalCompany;
  onEditAndSave?: (amount: number) => void;
  onDelete?: () => void;
  jobNumber: string;
  isLoading?: boolean;
  onReset?: () => void;
  jobStatus: JobStatus;
  isJobEditable: boolean;
}

const ExternalCompanyReferralTable: React.FC<ExternalCompanyReferralTableProps> = ({
  referralData,
  onEditAndSave,
  onDelete,
  onReset,
  jobNumber,
  isLoading = false,
  jobStatus,
  isJobEditable
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const currentTeamAccountingPermissions = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { overrideJobRatesAndCommissions } = currentTeamAccountingPermissions ?? {};
  const currentTeamJobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { modifyDoneJobs } = currentTeamJobPermissions ?? {};

  const canEditExternalCompanyReferral = () => {
    if (!isJobEditable) {
      return false;
    }

    if (jobStatus === JobStatus.DONE) {
      return overrideJobRatesAndCommissions && modifyDoneJobs;
    }
    return overrideJobRatesAndCommissions;
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };

  const handleSave = (amount: number) => {
    if (onEditAndSave) {
      onEditAndSave(amount);
    }
    setIsEditModalOpen(false);
  };

  return (
    <>
      <div className="w-full overflow-hidden rounded-lg border shadow-sm">
        <table className="w-full border-collapse bg-white">
          <thead className="border-b-2 bg-gray-50 text-sbase font-semibold text-[#878787]">
            <tr>
              <th className="w-1/4 py-2 px-4 text-left">External company</th>
              <th className="w-1/4 py-2 px-4 text-left">Referral on</th>
              <th className="w-1/4 py-2 px-4 text-left">Rate</th>
              <th className="w-1/4 py-2 px-4 text-left">Amount</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {referralData?.id ? (
              <tr className="align-top">
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">{referralData?.name}</td>
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                  {referralData.isEdited ? (
                    <span className="text-sbase font-bold text-primaryText">-</span>
                  ) : (
                    <div>
                      {referralData?.baseAmount ? `${getAmountWithCurrency(referralData?.baseAmount)}` : '-'}
                      <div className="mt-2 text-sbase font-medium text-accentText">
                        {CommissionBaseTypeLabels?.[referralData?.base]}
                      </div>
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sbase font-semibold text-primaryText">
                  {referralData?.isEdited || referralData?.referralType === CommissionType.ABSOLUTE ? (
                    <span className="text-sbase font-bold text-primaryText">-</span>
                  ) : (
                    <div>
                      {referralData?.referralValue ? (
                        <div className="text-sbase font-bold text-primaryText">{referralData.referralValue}%</div>
                      ) : (
                        <span className="text-sbase font-bold text-primaryText">-</span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sbase font-bold text-primaryText">
                  {referralData?.referralAmount ? `${getAmountWithCurrency(referralData.referralAmount)}` : '-'}
                </td>
                <td className="flex space-x-3 px-4 py-2 text-sm">
                  {referralData?.id && canEditExternalCompanyReferral() && (
                    <>
                      <button onClick={handleEditClick} className="transition-opacity hover:opacity-80">
                        <FormEditIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => setShowDeleteModal(true)} className="transition-opacity hover:opacity-80">
                        <DeleteIcon className="path path-fill-current h-5 w-5 text-accentText" />
                      </button>
                      <button
                        onClick={onReset}
                        disabled={!referralData?.isEdited || isLoading}
                        className={`transition-opacity ${
                          !isLoading && referralData?.isEdited ? 'opacity-100 hover:opacity-80' : 'opacity-50'
                        }`}>
                        <ResetIcon className="path path-fill-current h-5 w-5 text-accentText" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-2 text-center text-sbase font-bold text-primaryText">
                  No external company referral found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <GenericDeleteModal
        showModal={showDeleteModal}
        isActionInProgress={isLoading}
        title="Remove external company referral"
        subTitle="Are you sure you want to remove this external company from the job?"
        primaryBtnLabel="Remove"
        primaryBtnId="remove-external-company-referral-btn"
        onPrimaryBtnClick={() => {
          onDelete?.();
          setShowDeleteModal(false);
        }}
        secondaryBtnLabel="Cancel"
        secondaryBtnId="cancel-remove-external-company-referral-btn"
        onSecondaryBtnClick={() => setShowDeleteModal(false)}
      />

      {isEditModalOpen && (
        <EditReferralExpenseModal
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSave}
          jobNumber={jobNumber}
          currentAmount={referralData?.referralAmount}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ExternalCompanyReferralTable;
